import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { datricApi } from '../../../services/api/datric.api';
import { CreateUserPayload, UpdateUserPayload, User } from '../../../models/user.model';
import { toastService } from '../../../services/toast.service';

export interface UserState {
	list: User[];
	loading: boolean;
}

const initialState: UserState = {
	list: [],
	loading: false
};

export const fetchUsers = createAsyncThunk('user/list', async () => {
	return datricApi.user.list();
});

export const createUser = createAsyncThunk(
	'user/create',
	async (payload: CreateUserPayload, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.user.create(payload);
			toastService.success('Usuário criado');
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao criar usuário');
			return rejectWithValue(e);
		}
	}
);

export const editUser = createAsyncThunk(
	'user/edit',
	async (payload: { id: string } & UpdateUserPayload, { dispatch, rejectWithValue }) => {
		try {
			const { id, ...data } = payload;
			const res = await datricApi.user.update(id, data);
			toastService.success('Usuário atualizado');
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao atualizar usuário');
			return rejectWithValue(e);
		}
	}
);

export const setUserPassword = createAsyncThunk(
	'user/setPassword',
	async (payload: { id: string; newPassword: string }, { rejectWithValue }) => {
		try {
			const res = await datricApi.user.setPassword(payload.id, payload.newPassword);
			toastService.success('Senha redefinida');
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao redefinir senha');
			return rejectWithValue(e);
		}
	}
);

export const changeMyPassword = createAsyncThunk(
	'user/changeMyPassword',
	async (payload: { currentPassword: string; newPassword: string }, { rejectWithValue }) => {
		try {
			const res = await datricApi.user.changeMyPassword(payload.currentPassword, payload.newPassword);
			toastService.success('Senha alterada');
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao alterar senha');
			return rejectWithValue(e);
		}
	}
);

export const editUserPrice = createAsyncThunk(
	'user/editPrice',
	async (payload: { id: string; pricePerQuery: number }, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.user.updatePrice(payload.id, payload.pricePerQuery);
			toastService.success('Preço atualizado');
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao atualizar preço');
			return rejectWithValue(e);
		}
	}
);

export const adjustUserCredits = createAsyncThunk(
	'user/adjustCredits',
	async (payload: { id: string; amount: number; description?: string }, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.user.addCredits(payload.id, payload.amount, payload.description);
			toastService.success('Créditos ajustados');
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao ajustar créditos');
			return rejectWithValue(e);
		}
	}
);

export const removeUser = createAsyncThunk(
	'user/remove',
	async (payload: { id: string }, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.user.delete(payload.id);
			toastService.success('Usuário excluído');
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao excluir usuário');
			return rejectWithValue(e);
		}
	}
);

export const editUserLeilao = createAsyncThunk(
	'user/editLeilao',
	async (
		payload: { id: string; leilaoEnabled?: boolean; leilaoPriceToday?: number | null; leilaoPriceOld?: number | null },
		{ dispatch, rejectWithValue }
	) => {
		try {
			const { id, ...rest } = payload;
			const res = await datricApi.user.updateLeilao(id, rest);
			toastService.success('Leilão atualizado');
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao atualizar o leilão');
			return rejectWithValue(e);
		}
	}
);

export const toggleUserActive = createAsyncThunk(
	'user/toggleActive',
	async (payload: { id: string; active: boolean }, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.user.setActive(payload.id, payload.active);
			dispatch(fetchUsers());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao alterar status');
			return rejectWithValue(e);
		}
	}
);

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(fetchUsers.pending, state => {
			state.loading = true;
		});
		builder.addCase(fetchUsers.fulfilled, (state, action) => {
			state.loading = false;
			state.list = action.payload as User[];
		});
		builder.addCase(fetchUsers.rejected, state => {
			state.loading = false;
		});
	}
});
